import { BookingFormBase, validationRegex, isDateTimeInPast } from "./utils";
import { unlockDanhyLockedFields } from "./_QRLogin";

/**
 * CRM-integrated general booking.
 * Keeps the same UI/validation flow as other booking forms.
 * CRM delivery can be implemented later (e.g. via REST proxy). For now this submits via WP AJAX.
 *
 * Usage:
 *   import GeneralBookingFormCrm from "./_GeneralBookingFormCrm";
 *   const form = new GeneralBookingFormCrm({ dateTimeManager });
 */
export default class GeneralBookingFormCrm extends BookingFormBase {
  constructor({
    managers,
    dateTimeManager,
    formSelector = "#integrated-booking-form",
    formType = "integrated-crm",
  } = {}) {
    super(formSelector, {
      formType,
      dateTimeManager,
      managers,
    });

    if (!this.isActive) {
      return;
    }

    this.bindModalRelationLoadingListeners();
    this.initBase();
    this.initRelationData();
    this.initAppointmentTypes();
  }

  initRelationData() {
    if (!HM_Variables?.hospitals) {
      return;
    }

    const allHospitals = [...HM_Variables.hospitals];
    const availableHospitals = allHospitals.filter((hospital) => {
      const useExternalLink =
        hospital?.hospital_external_link?.use_external_link;
      return !useExternalLink;
    });

    const $form = this.$form;
    const $hospitalsSelect = $form.find("#hospitals-select-list");
    const $hospitalsInput = $form.find("input[name='booking-hospital']");

    $hospitalsSelect.html(
      availableHospitals
        .map((hospital) => {
          const shortName = hospital.hospital_shortname || "";
          return `<li class="option" data-value="${hospital.hospital_id}" data-hospital="${shortName}">${hospital.title}</li>`;
        })
        .join("")
    );

    this.initDefaultValues(availableHospitals);

    $form.find('input[name="phone"]').on("input.otpPhoneChange change.otpPhoneChange", () => {
      this.clearOtpVerificationToken();
    });

    $hospitalsInput.on("change", (e) => {
      const selectedHospitalId = $(e.target).val();
      if (!selectedHospitalId) {
        this.handleHospitalSelection(null);
        return;
      }

      const selectedHospital = availableHospitals.find(
        (hospital) => hospital.hospital_id == selectedHospitalId
      );
      if (!selectedHospital) {
        this.handleHospitalSelection(null);
        return;
      }

      this.handleHospitalSelection(selectedHospital);
    });
  }

  initAppointmentTypes() {
    if (
      typeof HM_Variables === "undefined" ||
      !Array.isArray(HM_Variables.appointment_types)
    ) {
      return;
    }

    const types = HM_Variables.appointment_types;
    const $form = this.$form;
    const $list = $form.find("#appointment-type-select-list");

    if (!$list.length) {
      return;
    }

    const lang = (HM_Variables.language || "vi").toLowerCase();
    const labelKey = lang === "en" ? "label_en" : "label_vi";

    const optionsHtml = types
      .map((type) => {
        const label = type[labelKey] || type.label_vi || type.label_en || "";
        return `<li class="option" data-value="${type.value}">${label}</li>`;
      })
      .join("");

    $list.html(optionsHtml);
  }

  initDefaultValues(allHospitals) {
    if (
      typeof bookingFormVars === "undefined" ||
      !bookingFormVars.book_hospital
    ) {
      return;
    }

    const bookHospital = bookingFormVars.book_hospital;

    const selectedHospital = allHospitals.find(
      (hospital) => hospital.id == bookHospital
    );

    if (!selectedHospital || !selectedHospital.hospital_id) {
      return;
    }

    const $form = this.$form;

    $form.find("input[name='booking-hospital']").val(selectedHospital.hospital_id);
    const $hospitalsCustomSelect = $form
      .find("#hospitals-select-list")
      .closest(".custom-select");
    const $hospitalsCurrentValue =
      $hospitalsCustomSelect.find(".current-value");
    $hospitalsCurrentValue
      .html(selectedHospital.title)
      .removeClass("default-value");

    $form.find("#hospitals-select-list .option").removeClass("active");
    $form
      .find(`#hospitals-select-list .option[data-value="${selectedHospital.hospital_id}"]`)
      .addClass("active");

    this.handleHospitalSelection(selectedHospital);
    this.applyUrlBookingPreselection();
  }

  validateFormRealtime() {
    const $submitBtn = this.$form.find('button[type="submit"]');

    const formData = {
      name: this.$form.find('input[name="name"]').val().trim(),
      email: this.$form.find('input[name="email"]').val().trim(),
      phone: this.$form.find('input[name="phone"]').val().trim(),
      birthday: this.$form.find('input[name="birthday"]').val().trim(),
      hospital: this.$form.find('input[name="booking-hospital"]').val().trim(),
      booking_time: this.$form.find('input[name="booking-time"]').val().trim(),
      appointment_type: this.$form.find('input[name="booking-appointment-type"]').val().trim(),
      agreement: this.$form.find('input[name="agreement"]').is(":checked"),
    };

    const isFormValid =
      formData.name &&
      formData.email &&
      formData.phone &&
      formData.birthday &&
      formData.hospital &&
      formData.appointment_type &&
      formData.booking_time &&
      formData.agreement;

    if (isFormValid) {
      $submitBtn.prop("disabled", false).removeClass("disabled");
    } else {
      $submitBtn.prop("disabled", true).addClass("disabled");
    }
  }

  getPastBookingTimeMessage() {
    return (
      HM_Variables?.frontend_lang?.booking_messages?.past_date_not_allowed ||
      HM_Variables?.frontend_lang?.validation?.booking_time_required ||
      (HM_Variables?.language === "en"
        ? "Please select a current or future date and time."
        : "Vui long chon ngay gio hien tai hoac trong tuong lai.")
    );
  }

  async ValidateForm() {
    let isValid = true;

    this.$form.find(".input-group").removeClass("err");
    this.$form.find(".err-msg").text("");
    this.hideFormError();

    const formData = {
      name: this.$form.find('input[name="name"]').val().trim(),
      email: this.$form.find('input[name="email"]').val().trim(),
      phone: this.$form.find('input[name="phone"]').val().trim(),
      address: (
        this.$form.find('input[name="dia_chi_khach_hang"]').val() ||
        this.$form.find('input[name="address"]').val() ||
        ""
      ).trim(),
      birthday: this.$form.find('input[name="birthday"]').val().trim(),
      cccd: this.$form.find('input[name="cccd"]').val().trim(),
      hospital: this.$form.find('input[name="booking-hospital"]').val().trim(),
      booking_time: this.$form.find('input[name="booking-time"]').val().trim(),
      additional_info: this.$form
        .find('[name="additional-information"]')
        .val()
        .trim(),
      agreement: this.$form.find('input[name="agreement"]').is(":checked"),
      // CRM extras populated by the datetime modal
      specialty: this.$form.find('input[name="booking-specialty"]').val().trim(),
      package: this.$form.find('input[name="booking-package"]').val().trim(),
      specialty_id: this.$form.find('input[name="booking-specialty-id"]').val().trim(),
      timeslot_id: this.$form.find('input[name="booking-timeslot-id"]').val().trim(),
      co_the_bhyt: this.$form.find('input[name="cothebhyt"]:checked').val() || "",
      appointment_type: this.$form.find('input[name="booking-appointment-type"]').val().trim(),
    };

    // Legacy datetime modal writes display value to booking-time-render.
    // Keep hidden booking-time in sync so submit validation does not fail
    // when hospital uses no_crm_integrated flow.
    if (!formData.booking_time) {
      const renderedBookingTime = this.$form
        .find('input[name="booking-time-render"]')
        .val()
        .trim();
      if (renderedBookingTime) {
        formData.booking_time = renderedBookingTime;
        this.$form.find('input[name="booking-time"]').val(renderedBookingTime);
      }
    }

    const resolvedHospital = this.resolveHospitalData(formData.hospital);
    const isNoCrmIntegratedHospital = !!resolvedHospital?.no_crm_integrated;

    if (!formData.name) {
      this.showError(
        'input[name="name"]',
        HM_Variables.frontend_lang.validation.name_required
      );
      isValid = false;
    } else if (formData.name.length > 64) {
      this.showError(
        'input[name="name"]',
        HM_Variables.frontend_lang.validation.name_too_long
      );
      isValid = false;
    } else if (!validationRegex.nameRegex.test(formData.name)) {
      this.showError(
        'input[name="name"]',
        HM_Variables.frontend_lang.validation.name_invalid
      );
      isValid = false;
    }

    if (!formData.email) {
      this.showError(
        'input[name="email"]',
        HM_Variables.frontend_lang.validation.email_required
      );
      isValid = false;
    } else if (formData.email.length > 100) {
      this.showError(
        'input[name="email"]',
        HM_Variables.frontend_lang.validation.email_too_long
      );
      isValid = false;
    } else if (!validationRegex.emailRegex.test(formData.email)) {
      this.showError(
        'input[name="email"]',
        HM_Variables.frontend_lang.validation.email_invalid
      );
      isValid = false;
    }

    if (!formData.phone) {
      this.showError(
        'input[name="phone"]',
        HM_Variables.frontend_lang.validation.phone_required
      );
      isValid = false;
    } else if (!validationRegex.phoneRegex.test(formData.phone)) {
      this.showError(
        'input[name="phone"]',
        HM_Variables.frontend_lang.validation.phone_invalid
      );
      isValid = false;
    }

    if (formData.address && formData.address.length > 200) {
      const addressSelector = this.$form.find('input[name="dia_chi_khach_hang"]').length
        ? 'input[name="dia_chi_khach_hang"]'
        : 'input[name="address"]';
      this.showError(
        addressSelector,
        HM_Variables.frontend_lang.validation.address_too_long
      );
      isValid = false;
    }

    if (formData.cccd && !validationRegex.cccdRegex.test(formData.cccd)) {
      this.showError(
        'input[name="cccd"]',
        HM_Variables.frontend_lang.validation.cccd_invalid
      );
      isValid = false;
    }

    if (!formData.birthday) {
      this.showError(
        'input[name="birthday"]',
        HM_Variables.frontend_lang.validation.birthday_required
      );
      isValid = false;
    }

    if (!formData.hospital) {
      this.showError(
        'input[name="booking-hospital"]',
        HM_Variables.frontend_lang.validation.hospital_required
      );
      isValid = false;
    }

    if (!formData.appointment_type) {
      this.showError(
        'input[name="booking-appointment-type"]',
        HM_Variables.frontend_lang.validation.appointment_type_required
      );
      isValid = false;
    }

    if (!formData.booking_time) {
      this.showError(
        'input[name="booking-time"]',
        HM_Variables.frontend_lang.validation.booking_time_required
      );
      isValid = false;
    } else if (!isNoCrmIntegratedHospital && isDateTimeInPast(formData.booking_time)) {
      this.showError(
        'input[name="booking-time"]',
        this.getPastBookingTimeMessage()
      );
      isValid = false;
    }

    if (!formData.agreement) {
      this.showAgreementError(
        HM_Variables.frontend_lang.validation.agreement_required
      );
      isValid = false;
    }
    
    if (isValid) {
      if (
        this.requiresOtpForSelectedHospital() &&
        $("#otp-verify-modal").length &&
        window.HM_OTP_FLOW
      ) {
        await this.startOtpVerification();
      } else {
        await this.submitForm();
      }
    }

    return isValid;
  }

  resetCustomSelects() {
    const $hospitalsCustomSelect = this.$form
      .find("#hospitals-select-list")
      .closest(".custom-select");
    const $hospitalsCurrentValue =
      $hospitalsCustomSelect.find(".current-value");
    $hospitalsCurrentValue
      .html(HM_Variables.frontend_lang.select_placeholders.choose_hospital)
      .addClass("default-value");
    this.$form.find("input[name='booking-hospital']").val("");

    this.handleHospitalSelection(null);
  }

  clearDanhySessionUiAfterSubmit() {
    if (this.$form.attr("data-danhy-logged-in") !== "1") {
      return;
    }

    unlockDanhyLockedFields(this.$form);
    $(".booking-form-crm-danhy").prop("hidden", false);
  }

  async submitForm(options = {}) {
    const { fromOtpFlow = false } = options;
    const $submitBtn = this.$form.find('button[type="submit"]');
    const originalText = $submitBtn.text();

    const processingText =
      HM_Variables.language === "vi" ? "Đang xử lý..." : "Processing...";

    if (!fromOtpFlow) {
      $submitBtn.prop("disabled", true).text(processingText);
    }

    this.hideFormError();

    const connectionError =
      HM_Variables.language === "vi"
        ? "Lỗi kết nối. Vui lòng thử lại."
        : "Connection error. Please try again.";

    try {
      const token = await this.getRecaptchaToken().catch(() => "");

      const formData = new FormData(this.$form[0]);

      const $recaptchaInput = this.$form.find("#g-recaptcha-response");
      if ($recaptchaInput.length) {
        formData.set("g-recaptcha-response", token);
      } else {
        formData.append("g-recaptcha-response", token);
      }

      return await new Promise((resolve, reject) => {
        $.ajax({
          url: HM_Variables.ajax_url,
          type: "POST",
          data: formData,
          processData: false,
          contentType: false,
          success: (response) => {
            if (!fromOtpFlow) {
              $submitBtn
                .prop("disabled", false)
                .text(originalText)
                .removeClass("disabled");
            }

            if (response.success) {
              this.clearOtpVerificationToken();
              this.clearDanhySessionUiAfterSubmit();

              if (fromOtpFlow && window.HM_OTP_FLOW) {
                window.HM_OTP_FLOW.closeModal({ clearResendTimer: true });
              }

              this.showThankYouModal();
              this.$form[0].reset();
              this.resetCustomSelects();
              this.validateFormRealtime();
              resolve(response);
              return;
            }

            const errorMessage = response.data || connectionError;

            if (this.$form.attr("data-danhy-logged-in") === "1") {
              this.clearDanhySessionUiAfterSubmit();
            }

            if (fromOtpFlow && window.HM_OTP_FLOW) {
              window.HM_OTP_FLOW.closeModal();
              this.showThankYouModal({
                isError: true,
                message: errorMessage,
              });
            } else {
              this.showFormError(errorMessage);
            }

            reject(response);
          },
          error: (xhr) => {
            if (!fromOtpFlow) {
              $submitBtn
                .prop("disabled", false)
                .text(originalText)
                .removeClass("disabled");
            }

            const errorMessage = xhr.responseJSON?.data || connectionError;

            if (this.$form.attr("data-danhy-logged-in") === "1") {
              this.clearDanhySessionUiAfterSubmit();
            }

            if (fromOtpFlow && window.HM_OTP_FLOW) {
              window.HM_OTP_FLOW.closeModal();
              this.showThankYouModal({
                isError: true,
                message: errorMessage,
              });
            } else {
              this.showFormError(errorMessage);
            }

            reject(xhr);
          },
        });
      });
    } catch (error) {
      if (!fromOtpFlow) {
        $submitBtn
          .prop("disabled", false)
          .text(originalText)
          .removeClass("disabled");
      }

      const errorMessage = error?.message || connectionError;

      if (this.$form.attr("data-danhy-logged-in") === "1") {
        this.clearDanhySessionUiAfterSubmit();
      }

      if (fromOtpFlow && window.HM_OTP_FLOW) {
        window.HM_OTP_FLOW.closeModal();
        this.showThankYouModal({
          isError: true,
          message: errorMessage,
        });
      } else {
        this.showFormError(errorMessage);
      }

      throw error;
    }
  }
}

